import { useEffect, useRef, useState } from "react";
import Icon from "../Icon";
import { usePosicionDesplegable } from "../posicionDesplegable";

// Menu «Mas» para las acciones de uso poco frecuente de una cabecera: dejan sitio a las que
// se usan a diario sin quitarle ninguna funcion a nadie.
//
// acciones: [{ clave, etiqueta, icono, titulo, onClick }]. Sin acciones no pinta nada.
// Teclado: Enter/Espacio o flecha abajo abren, flechas mueven, Escape cierra y devuelve el foco.
export default function MenuMas({ acciones = [], etiqueta = "Más", soloIcono = false }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  const boton = useRef(null);
  const panelRef = useRef(null);
  const { estilo } = usePosicionDesplegable(abierto, ref, panelRef, { separacion: 6 });

  useEffect(() => {
    if (!abierto) return undefined;
    panelRef.current?.querySelector("[role=menuitem]")?.focus();
    const fuera = e => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  if (!acciones.length) return null;

  const teclado = e => {
    const items = [...panelRef.current.querySelectorAll("[role=menuitem]")];
    const i = items.indexOf(document.activeElement);
    if (e.key === "Escape") { e.preventDefault(); setAbierto(false); boton.current?.focus(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
    else if (e.key === "Tab") setAbierto(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", alignItems: "center" }}>
      {soloIcono ? (
        // Variante de una fila de lista: solo los tres puntos, con el nombre en aria-label.
        <button ref={boton} type="button" className="btn mt-mas" aria-haspopup="menu" aria-expanded={abierto}
          aria-label={etiqueta} title="Más acciones" onClick={() => setAbierto(a => !a)}
          style={{ display: "flex", alignItems: "center", borderRadius: 8, cursor: "pointer",
            border: "1px solid " + (abierto ? "var(--border)" : "transparent"),
            background: abierto ? "var(--surface2)" : "none", color: "var(--text2)" }}>
          <Icon name="three-dots" size={16} color="currentColor" />
        </button>
      ) : (
      <button ref={boton} type="button" className="btn" aria-haspopup="menu" aria-expanded={abierto}
        onClick={() => setAbierto(a => !a)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 11px", borderRadius: 9, cursor: "pointer",
          fontSize: 11.5, fontFamily: "var(--font)", whiteSpace: "nowrap", border: "1px solid var(--border-ctl)",
          background: "var(--surface2)", color: "var(--text2)" }}>
        {etiqueta}<Icon name="chevron-down" size={10} color="var(--text3)" />
      </button>
      )}
      {abierto && (
        <div ref={panelRef} role="menu" aria-label={etiqueta} onKeyDown={teclado}
          style={{ zIndex: 500, minWidth: 210, background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: 12, boxShadow: "var(--shadow-lg)", padding: 5, display: "flex", flexDirection: "column", ...estilo }}>
          {acciones.map(a => (
            <button key={a.clave} type="button" role="menuitem" className="btn" title={a.titulo}
              onClick={() => { setAbierto(false); a.onClick(); }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, border: "none",
                background: "none", cursor: "pointer", textAlign: "left", fontSize: 12, fontFamily: "var(--font)", color: "var(--text)" }}>
              {a.icono && <Icon name={a.icono} size={12} color="var(--text3)" />}{a.etiqueta}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
